import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2, Circle, ChevronRight, ArrowRight,
  Building2, Target, Layers, FileQuestion, Users,
  Settings, Mail, LayoutDashboard, AlertCircle,
  Globe, User, UsersRound, Tag, Clock,
  HelpCircle, Pencil, Bell, Save, Loader2, AlertTriangle, Lock, Rocket
} from 'lucide-react';

// Common timezones for selection
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
];

/* step status: complete | partial | empty */
function stepStatus(count, required = 1) {
  if (count >= required) return 'complete';
  if (count > 0) return 'partial';
  return 'empty';
}

function StepIcon({ status }) {
  if (status === 'complete') return <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />;
  if (status === 'partial') return <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />;
  return <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />;
}

function StepCard({ icon: Icon, label, count, status, onClick, sub }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-slate-50 
                 transition-all text-left w-full group"
      data-testid={`step-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <StepIcon status={status} />
      <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 truncate">{label}</p>
        {sub && <p className="text-xs text-slate-400 truncate">{sub}</p>}
      </div>
      {count !== undefined && (
        <Badge variant={status === 'complete' ? 'default' : 'secondary'}
               className={`text-xs ${status === 'complete' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}`}>
          {count}
        </Badge>
      )}
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
    </button>
  );
}

function Connector() {
  return (
    <div className="flex justify-center py-1">
      <div className="w-px h-6 bg-slate-200" />
    </div>
  );
}

export default function AdminDashboardPage() {
  const { projectId } = useParams();
  const { currentProject } = useProject();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    project: null,
    drivers: [],
    segmentTypes: [],
    segments: [],
    questions: [],
    mappingCategories: [],
    stakeholderTotal: 0, // Invited + Reference only
    pendingCount: 0,     // Pending approval count
    dashConfig: null,
    emailConfig: null,
    notificationSettings: null,
  });

  // Edit project modal
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ 
    name: '', description: '', project_manager: '', contact_email: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Notification settings state
  const [notifSettings, setNotifSettings] = useState({
    new_submission_mode: 'immediate',
    admin_timezone: 'America/New_York',
  });
  const [savingNotif, setSavingNotif] = useState(false);
  const [pendingNotifCount, setPendingNotifCount] = useState(0);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Anonymity settings state
  const [anonymityThreshold, setAnonymityThreshold] = useState(3);
  const [savingAnonymity, setSavingAnonymity] = useState(false);
  
  // Access gate settings
  const [requireAboutMe, setRequireAboutMe] = useState(false);
  const [minGalaxyMappings, setMinGalaxyMappings] = useState(0);
  const [savingGates, setSavingGates] = useState(false);
  const [previewAboutMeModal, setPreviewAboutMeModal] = useState(false);
  const [previewGalaxyModal, setPreviewGalaxyModal] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        setLoading(true);
        const [pRes, dRes, stRes, sRes, qRes, mcRes, publicStatsRes, dcRes, ecRes, nsRes, npcRes] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/drivers`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/segment-types`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/segments`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/questions`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/mapping-categories`).catch(() => ({ data: { categories: [] } })),
          api.get(`/projects/${projectId}/stakeholders/public-stats`).catch(() => ({ data: { matrix: {} } })),
          api.get(`/projects/${projectId}/dashboard-config`).catch(() => ({ data: null })),
          api.get(`/projects/${projectId}/email-config`).catch(() => ({ data: null })),
          api.get(`/projects/${projectId}/notification-settings`).catch(() => ({ data: null })),
          api.get(`/projects/${projectId}/notification-settings/pending-count`).catch(() => ({ data: { pending_count: 0 } })),
        ]);

        const project = pRes.data;
        const matrix = publicStatsRes.data?.matrix || {};
        
        // Calculate stakeholder total: Invited + Reference only
        const invitedTotal = matrix.invited?.total || 0;
        const referenceTotal = matrix.reference?.total || 0;
        const stakeholderTotal = invitedTotal + referenceTotal;
        
        // Pending count from pending_review
        const pendingCount = matrix.pending_review?.total || 0;
        
        setData({
          project,
          drivers: dRes.data || [],
          segmentTypes: stRes.data || [],
          segments: sRes.data || [],
          questions: qRes.data || [],
          mappingCategories: mcRes.data?.categories || mcRes.data || [],
          stakeholderTotal,
          pendingCount,
          dashConfig: dcRes.data,
          emailConfig: ecRes.data,
          notificationSettings: nsRes.data,
        });
        setEditForm({ 
          name: project?.name || '', 
          description: project?.description || '',
          project_manager: project?.project_manager || '',
          contact_email: project?.contact_email || '',
        });
        
        // Set notification settings
        if (nsRes.data) {
          setNotifSettings(nsRes.data);
        }
        setPendingNotifCount(npcRes.data?.pending_count || 0);
        
        // Set anonymity threshold from dashboard config
        if (dcRes.data?.anonymity_threshold) {
          setAnonymityThreshold(dcRes.data.anonymity_threshold);
        }
        // Set access gate settings
        if (dcRes.data) {
          setRequireAboutMe(dcRes.data.require_about_me_completion || false);
          setMinGalaxyMappings(dcRes.data.min_galaxy_mappings || 0);
        }
      } catch (e) {
        console.error('Admin dashboard fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const nav = (path) => navigate(`/project/${projectId}${path}`);

  const questionCounts = useMemo(() => {
    const qs = data.questions;
    return {
      public: qs.filter(q => q.question_context === 'public_landing').length,
      aboutMe: qs.filter(q => q.question_context === 'about_me').length,
      aboutOthers: qs.filter(q => q.question_context === 'about_others').length,
      total: qs.length,
    };
  }, [data.questions]);

  const completedSteps = useMemo(() => {
    let count = 0;
    if (data.project?.name) count++;
    if (data.drivers.length > 0) count++;
    if (data.segments.length > 0) count++;
    if (data.questions.length > 0) count++;
    if (data.stakeholderTotal > 0) count++;
    if (data.dashConfig) count++;
    if (data.notificationSettings) count++;
    if (anonymityThreshold > 0) count++; // Anonymity settings always complete if set
    if (data.emailConfig) count++;
    return count;
  }, [data, anonymityThreshold]);

  const totalSteps = 9;

  const handleSaveProject = async () => {
    try {
      setSaving(true);
      await api.put(`/projects/${projectId}`, editForm);
      setData(prev => ({ ...prev, project: { ...prev.project, ...editForm } }));
      setShowEditDialog(false);
    } catch (e) {
      console.error('Failed to save project:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifSettings = async () => {
    try {
      setSavingNotif(true);
      await api.put(`/projects/${projectId}/notification-settings`, notifSettings);
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error('Failed to save notification settings');
    } finally {
      setSavingNotif(false);
    }
  };

  const handleSendTestSummary = async () => {
    if (pendingNotifCount === 0) {
      toast.info('No pending notifications to send');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.post(`/projects/${projectId}/notification-settings/test-daily-summary`);
      toast.success(`Daily summary sent! ${res.data.count} notification(s) included.`);
      // Refresh pending count
      const npcRes = await api.get(`/projects/${projectId}/notification-settings/pending-count`);
      setPendingNotifCount(npcRes.data?.pending_count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test summary');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveAnonymity = async () => {
    try {
      setSavingAnonymity(true);
      await api.put(`/projects/${projectId}/dashboard-config`, { anonymity_threshold: anonymityThreshold });
      toast.success('Anonymity threshold saved');
    } catch (e) {
      toast.error('Failed to save anonymity threshold');
    } finally {
      setSavingAnonymity(false);
    }
  };

  const handleSaveGates = async () => {
    try {
      setSavingGates(true);
      await api.put(`/projects/${projectId}/dashboard-config`, { 
        require_about_me_completion: requireAboutMe,
        min_galaxy_mappings: minGalaxyMappings 
      });
      toast.success('Access settings saved');
    } catch (e) {
      toast.error('Failed to save access settings');
    } finally {
      setSavingGates(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5AA2AE]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6 text-[#5AA2AE]" />
            Admin Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {currentProject?.name || 'Project'} setup progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm py-1 px-3">
            {completedSteps}/{totalSteps} steps
          </Badge>
          <Button variant="outline" size="sm" onClick={() => nav('/admin-help')} data-testid="admin-help-btn">
            <HelpCircle className="w-4 h-4 mr-1" /> Setup Guide
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`/admin-onboarding/${projectId}`, '_blank')} data-testid="admin-intro-btn">
            <Rocket className="w-4 h-4 mr-1" /> Pulse Introduction
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div
          className="bg-[#5AA2AE] h-2 rounded-full transition-all duration-500"
          style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
          data-testid="progress-bar"
        />
      </div>

      {/* Flow Chart */}
      <div className="space-y-1">

        {/* Section 1: Project Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Building2 className="w-4 h-4 text-[#5AA2AE]" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <StepCard
              icon={Pencil}
              label="Project Details"
              status={data.project?.name ? 'complete' : 'empty'}
              sub={data.project?.name || 'Not configured'}
              onClick={() => setShowEditDialog(true)}
            />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 2: Drivers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Target className="w-4 h-4 text-[#5AA2AE]" />
              Drivers
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <StepCard
              icon={Target}
              label="Drivers"
              count={data.drivers.length}
              status={stepStatus(data.drivers.length)}
              onClick={() => nav('/drivers')}
            />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 3: Segments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Layers className="w-4 h-4 text-[#5AA2AE]" />
              Segments
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <StepCard
              icon={Layers}
              label="Segment Types"
              count={data.segmentTypes.length}
              status={stepStatus(data.segmentTypes.length)}
              sub={data.segmentTypes.map(st => st.name).join(', ') || 'None defined'}
              onClick={() => nav('/segments')}
            />
            <StepCard
              icon={Tag}
              label="Segments"
              count={data.segments.length}
              status={stepStatus(data.segments.length)}
              onClick={() => nav('/segments')}
            />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 4: Questions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <FileQuestion className="w-4 h-4 text-[#5AA2AE]" />
              Questions
              {questionCounts.total > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{questionCounts.total} total</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <StepCard icon={Globe} label="Public" count={questionCounts.public}
              status={stepStatus(questionCounts.public)}
              onClick={() => nav('/questions?tab=public_landing')} />
            <StepCard icon={User} label="About Me" count={questionCounts.aboutMe}
              status={stepStatus(questionCounts.aboutMe)}
              onClick={() => nav('/questions?tab=about_me')} />
            <StepCard icon={UsersRound} label="About Others" count={questionCounts.aboutOthers}
              status={stepStatus(questionCounts.aboutOthers)}
              onClick={() => nav('/questions?tab=about_others')} />
            <StepCard icon={Tag} label="Mapping Categories" count={Array.isArray(data.mappingCategories) ? data.mappingCategories.length : 0}
              status={stepStatus(Array.isArray(data.mappingCategories) ? data.mappingCategories.length : 0, 0)}
              onClick={() => nav('/questions?tab=mapping')}
              sub="Optional" />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 5: Stakeholders */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Users className="w-4 h-4 text-[#5AA2AE]" />
              Stakeholders
              {data.stakeholderTotal > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{data.stakeholderTotal} total</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <StepCard icon={Clock} label="Pending" count={data.pendingCount}
              status={data.pendingCount > 0 ? 'partial' : 'empty'}
              onClick={() => nav('/stakeholders?tab=public')} />
            <StepCard icon={Users} label="Stakeholders" count={data.stakeholderTotal}
              status={stepStatus(data.stakeholderTotal)}
              onClick={() => nav('/stakeholders?tab=all')} />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 6: Dashboard Configuration */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Settings className="w-4 h-4 text-[#5AA2AE]" />
              Dashboard Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <StepCard
              icon={Settings}
              label="Permissions"
              status={data.dashConfig ? 'complete' : 'empty'}
              sub="Configure dashboard question mapping"
              onClick={() => nav('/dashboards/summary')}
            />
          </CardContent>
        </Card>

        <Connector />

        {/* Section 7: Notification Settings */}
        <Card data-testid="notification-settings-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Bell className="w-4 h-4 text-[#5AA2AE]" />
              Notification Settings
            </CardTitle>
            <CardDescription className="text-xs">
              Configure how you receive notifications about new stakeholder submissions
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {/* Notification Mode */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">New Submission Notifications</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <input
                    type="radio"
                    name="notification_mode"
                    value="immediate"
                    checked={notifSettings.new_submission_mode === 'immediate'}
                    onChange={() => setNotifSettings(s => ({ ...s, new_submission_mode: 'immediate' }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-600" />
                      <span className="font-medium text-sm">Immediate</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Receive an email immediately when a new stakeholder submits feedback
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <input
                    type="radio"
                    name="notification_mode"
                    value="daily_summary"
                    checked={notifSettings.new_submission_mode === 'daily_summary'}
                    onChange={() => setNotifSettings(s => ({ ...s, new_submission_mode: 'daily_summary' }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-600" />
                      <span className="font-medium text-sm">Daily Summary</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Receive a daily digest at 9 AM with all new submissions
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Timezone Selection (only for daily summary) */}
            {notifSettings.new_submission_mode === 'daily_summary' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Your Timezone</Label>
                <Select 
                  value={notifSettings.admin_timezone} 
                  onValueChange={(val) => setNotifSettings(s => ({ ...s, admin_timezone: val }))}
                >
                  <SelectTrigger className="w-full max-w-xs" data-testid="timezone-select">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Daily summary emails will be sent at 9:00 AM in this timezone
                </p>
                
                {/* Pending Notifications & Test Button */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-slate-900">Pending Notifications</h4>
                      <p className="text-xs text-slate-500">
                        {pendingNotifCount === 0 
                          ? 'No pending notifications'
                          : `${pendingNotifCount} notification${pendingNotifCount !== 1 ? 's' : ''} queued`
                        }
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendTestSummary}
                      disabled={sendingTest || pendingNotifCount === 0}
                      data-testid="send-test-summary"
                    >
                      {sendingTest ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</>
                      ) : (
                        <><Mail className="w-3 h-3 mr-1" /> Send Now</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleSaveNotifSettings} 
                disabled={savingNotif}
                size="sm"
                className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
                data-testid="save-notification-settings"
              >
                {savingNotif ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        <Connector />

        {/* Section 8: Anonymity Settings */}
        <Card data-testid="anonymity-settings-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Anonymity Settings
            </CardTitle>
            <CardDescription className="text-xs">
              Protect stakeholder privacy by hiding data below a threshold
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Label className="text-sm font-medium">Minimum responses threshold:</Label>
              <Select 
                value={String(anonymityThreshold)} 
                onValueChange={(val) => setAnonymityThreshold(parseInt(val))}
              >
                <SelectTrigger className="w-full sm:w-40" data-testid="anonymity-threshold-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 responses</SelectItem>
                  <SelectItem value="5">5 responses</SelectItem>
                  <SelectItem value="10">10 responses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              Data will be hidden if the number of responses is below this threshold to protect anonymity.
            </p>
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveAnonymity} 
                disabled={savingAnonymity}
                size="sm"
                className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
                data-testid="save-anonymity-settings"
              >
                {savingAnonymity ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        <Connector />

        {/* Section 9: Access Gates */}
        <Card data-testid="access-gates-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Lock className="w-4 h-4 text-[#5AA2AE]" />
              Dashboard Access Requirements
            </CardTitle>
            <CardDescription className="text-xs">
              Control when users can access dashboards based on their completion progress
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 space-y-5">
            {/* About Me Completion Gate */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Require About Me Completion</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Non-admin users must complete all About Me questions before accessing any dashboard
                  </p>
                  <button
                    type="button"
                    onClick={() => setPreviewAboutMeModal(true)}
                    className="text-xs text-[#5AA2AE] hover:underline mt-1 inline-block"
                    data-testid="preview-about-me-modal"
                  >
                    Preview what users will see
                  </button>
                </div>
                <Switch
                  checked={requireAboutMe}
                  onCheckedChange={setRequireAboutMe}
                  data-testid="require-about-me-toggle"
                />
              </div>
            </div>

            {/* Galaxy Minimum Mappings */}
            <div className="space-y-3 pt-3 border-t">
              <div>
                <Label className="text-sm font-medium">Minimum Mappings for People Galaxy</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Set the minimum number of About Others stakeholder mappings required before People Galaxy becomes visible. Set to 0 to disable.
                </p>
                <button
                  type="button"
                  onClick={() => setPreviewGalaxyModal(true)}
                  className="text-xs text-[#5AA2AE] hover:underline mt-1 inline-block"
                  data-testid="preview-galaxy-modal"
                >
                  Preview what users will see
                </button>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  value={String(minGalaxyMappings)}
                  onValueChange={(val) => setMinGalaxyMappings(parseInt(val))}
                >
                  <SelectTrigger className="w-full sm:w-48" data-testid="min-galaxy-mappings-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Disabled (no minimum)</SelectItem>
                    <SelectItem value="1">1 mapping</SelectItem>
                    <SelectItem value="2">2 mappings</SelectItem>
                    <SelectItem value="3">3 mappings</SelectItem>
                    <SelectItem value="5">5 mappings</SelectItem>
                    <SelectItem value="10">10 mappings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveGates}
                disabled={savingGates}
                size="sm"
                className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
                data-testid="save-access-gates"
              >
                {savingGates ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        <Connector />

        {/* Section 10: Email Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <Mail className="w-4 h-4 text-[#5AA2AE]" />
              Email Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <StepCard
              icon={Mail}
              label="Email Configuration"
              status={data.emailConfig?.from_name || data.emailConfig?.reminder_enabled ? 'complete' : 'empty'}
              sub={data.emailConfig?.from_name ? `From: ${data.emailConfig.from_name}` : 'Not configured'}
              onClick={() => nav('/stakeholders?tab=email-settings')}
            />
          </CardContent>
        </Card>
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="edit-project-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-desc">Description</Label>
              <Textarea
                id="project-desc"
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="edit-project-description"
              />
              <p className="text-xs text-slate-400 flex items-start gap-1.5" data-testid="description-ai-note">
                <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#5AA2AE]" />
                A meaningful description helps the AI contextualize responses and generate more relevant insights.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-pm" className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Project Manager
                </Label>
                <Input
                  id="edit-pm"
                  value={editForm.project_manager}
                  onChange={(e) => setEditForm(prev => ({ ...prev, project_manager: e.target.value }))}
                  data-testid="edit-project-manager"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Contact Email
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, contact_email: e.target.value }))}
                  data-testid="edit-contact-email"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveProject} 
                disabled={saving} 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                data-testid="save-project-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview: About Me Gate Modal */}
      <Dialog open={previewAboutMeModal} onOpenChange={setPreviewAboutMeModal}>
        <DialogContent className="sm:max-w-md" data-testid="preview-about-me-gate-modal">
          <div className="absolute top-2 left-3">
            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 bg-amber-50">Admin Preview</Badge>
          </div>
          <DialogHeader className="pt-4">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-[#5AA2AE]" />
              Complete Your Profile First
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Before diving into the dashboards, we'd love to learn a bit more about you! Please complete the questions in your <strong>About Me</strong> section. This helps us personalize your experience and provide meaningful insights.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Your progress</p>
                <p className="text-xs text-slate-500">3 of 8 questions answered</p>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#5AA2AE" strokeWidth="4"
                    strokeDasharray={`${(3 / 8) * 175.9} 175.9`}
                    strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-700">38%</span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPreviewAboutMeModal(false)}>Maybe Later</Button>
            <Button className="bg-[#5AA2AE] hover:bg-[#4a8a94]" onClick={() => setPreviewAboutMeModal(false)}>Go to About Me</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview: Galaxy Gate Modal */}
      <Dialog open={previewGalaxyModal} onOpenChange={setPreviewGalaxyModal}>
        <DialogContent className="sm:max-w-md" data-testid="preview-galaxy-gate-modal">
          <div className="absolute top-2 left-3">
            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 bg-amber-50">Admin Preview</Badge>
          </div>
          <DialogHeader className="pt-4">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <UsersRound className="h-5 w-5 text-[#5AA2AE]" />
              Map More Stakeholders
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              The People Galaxy comes to life when you've mapped enough stakeholders. Add a few more people in your <strong>About Others</strong> section to unlock this view and see how your network connects!
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Your mappings</p>
                <p className="text-xs text-slate-500">1 of {minGalaxyMappings || 3} required mappings</p>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: minGalaxyMappings || 3 }, (_, i) => (
                  <div key={i} className={`w-3 h-3 rounded-full ${i < 1 ? 'bg-[#5AA2AE]' : 'bg-slate-200'}`} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPreviewGalaxyModal(false)}>Maybe Later</Button>
            <Button className="bg-[#5AA2AE] hover:bg-[#4a8a94]" onClick={() => setPreviewGalaxyModal(false)}>Go to About Others</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
